import {
  always,
  cond,
  T,
  last,
  propEq,
  map,
  find,
  flatten,
  pluck,
  tail,
  reduce,
  assocPath,
  keys,
  hasPath,
  lensPath,
  set,
} from "ramda"
import { isAndroidDevice, isFeatureEnabled, isIosDevice } from "js/includes/common/utils"
import { getReadableBytes } from "js/includes/common/_conversions"
import { getNodeRoles } from "js/includes/common/client"
import { restrictionsPolicyConfiguration } from "js/includes/editors/Policy/PolicyEditor/formComponents/restrictionsPolicyConfiguration"

export const mdmScopeOptionsEnum = {
  DIVISION: "DIVISION",
  ORGANIZATION: "ORGANIZATION",
  NOT_CONFIGURED: "NOT_CONFIGURED",
}

export const isMDMEnabled = () => isFeatureEnabled("mdm_android") || isFeatureEnabled("mdm_apple")

export const isMDMConfigurationsEnabled = () => window.store.getState()?.session?.divisionConfigs?.mdm?.enabled

export const isAndroidMDMEnabled = () => isFeatureEnabled("mdm_android") && isMDMConfigurationsEnabled()

export const isAppleMDMEnabled = () => isFeatureEnabled("mdm_apple") && isMDMConfigurationsEnabled()

export const getMobileData = async (content, mobileNode) => {
  const { node } = content
  const { name: friendlyName, datasets, nodeClass, nodeId, description, locationId } = node || {}
  const {
    osVersion,
    osBuildNumber,
    brand,
    model,
    enrollmentType,
    ownership,
    policyCompliant,
    enrollmentTime,
    phoneNumber,
    imei,
    isEncrypted,
    serialNumber,
    deviceKernelVersion,
    deviceType,
    hardware,
    manufacturer,
    name,
  } = datasets?.[0]?.datapoints?.[0]?.data || {}
  const { node: mobileNodeData } = mobileNode
  const {
    ipAddress,
    mdmStatus,
    netbiosName,
    systemName,
    policyContent,
    policyId,
    policyIdSource,
    policyName,
    approvalStatus,
    approvalStatusByAppUserId,
    approvalStatusByAppUserName,
    approvalStatusTime,
    clientId,
    clientName,
    nodeRoleId,
  } = mobileNodeData

  const nodeRoleData = await getNodeRoles()
  const currentNodeRole = find(propEq("id", nodeRoleId))(nodeRoleData)
  const nodeRole = currentNodeRole?.name ?? ""

  return {
    node: {
      name,
      systemName,
      netbiosName,
      ipAddress,
      friendlyName,
      osVersion,
      osBuildNumber,
      brand,
      model,
      enrollmentType,
      ownership,
      policyCompliant,
      enrollmentTime,
      phoneNumber,
      imei,
      isEncrypted,
      serialNumber,
      deviceKernelVersion,
      deviceType,
      hardware,
      manufacturer,
      nodeClass,
      nodeId,
      description,
      locationId,
      id: nodeId,
      nodeRoleId,
      nodeRole,
      mdmStatus,
      policyContent,
      policyId,
      policyIdSource,
      policyName,
      approvalStatus,
      approvalStatusByAppUserId,
      approvalStatusByAppUserName,
      approvalStatusTime,
      clientId,
      clientName,
    },
  }
}

export const getMobileDeviceType = cond([
  [isAndroidDevice, always("android")],
  [isIosDevice, always("apple_ios")],
  [T, always("mobile")],
])

export const mapMemoryData = resultData => {
  const {
    node: { datasets },
  } = resultData
  const memoryDataset = datasets.find(propEq("dataspecName", "mdm-memory"))

  if (memoryDataset?.datapoints?.length) {
    const { datapoints } = memoryDataset
    if (datapoints?.length) {
      const data = flatten(pluck("volatileData")(tail(datapoints)))
      const groupByMemoryType = data =>
        reduce(
          (groupToMap, m) => {
            groupToMap[m.memoryType].push({
              ...m,
              pct: Math.floor(m.utilizationPercentage * 100),
              total: getReadableBytes(m.capacityBytes),
              used: getReadableBytes(m.usedBytes),
            })
            return groupToMap
          },
          { RAM_MEASURED: [], INTERNAL_STORAGE_MEASURED: [], EXTERNAL_STORAGE_MEASURED: [] },
          data,
        )
      const groupedData = groupByMemoryType(data)
      const summaryData = groupByMemoryType(datapoints[0].staticData)

      const { RAM_MEASURED: memory, INTERNAL_STORAGE_MEASURED: internal, EXTERNAL_STORAGE_MEASURED: external } = map(
        last,
      )(summaryData)
      const {
        RAM_MEASURED: memoryData,
        INTERNAL_STORAGE_MEASURED: internalStorageData,
        EXTERNAL_STORAGE_MEASURED: externalStorageData,
      } = groupedData

      const lastMemory = memoryData && last(memoryData)
      const lastInternalStorageData = internalStorageData && last(internalStorageData)
      const lastExternalStorageData = externalStorageData && last(externalStorageData)
      return {
        memoryData,
        internalStorageData,
        externalStorageData,
        summary: {
          memory: {
            ...memory,
            pct: lastMemory?.pct || 0,
            used: lastMemory?.used || 0,
          },
          internal: {
            ...internal,
            pct: lastInternalStorageData?.pct || 0,
            used: lastInternalStorageData?.used || 0,
          },
          external: {
            ...external,
            pct: lastExternalStorageData?.pct || 0,
            used: lastExternalStorageData?.used || 0,
          },
        },
      }
    }
  }

  return {}
}

export const getDisplayName = node => node?.friendlyName || node?.name || ""

export const mdmScopeOptions = [
  {
    scope: "SCOPE_UNSPECIFIED",
    sectionTitle: "mdm.policies.devicePasscode",
  },
]

export const getEnabledRestrictions = restrictions => {
  let updatedRestrictions = { ...restrictions }
  const updateRestrictionValues = category =>
    map(key => {
      const restrictionLensPath = lensPath([category, key])
      updatedRestrictions = set(restrictionLensPath, true, updatedRestrictions)
    })(keys(updatedRestrictions[category]))

  map(updateRestrictionValues)(keys(updatedRestrictions))
  return updatedRestrictions
}

export const setRestrictionsValues = (updatedRestrictions, restrictions) => {
  let newRestrictions = {}
  const updateRestrictionValues = category =>
    map(key => {
      const defaultValue = find(propEq("key", key))(restrictionsPolicyConfiguration)
      const restrictionValue = hasPath([category, key], restrictions) ? restrictions[category][key] : defaultValue.value
      newRestrictions = assocPath([category, key], restrictionValue, newRestrictions)
    })(keys(updatedRestrictions[category]))
  map(updateRestrictionValues)(keys(updatedRestrictions))
  return newRestrictions
}

export const getApplicationsList = ({ node }) => {
  const dataSpec = find(propEq("dataspecName", "mdm-applicationreport"))(node.datasets)
  return dataSpec?.datapoints[0]?.data ?? []
}
